/**
 * PlanCard - Individual plan display card.
 *
 * Shows plan name, price, structured limits, features list,
 * and contextual CTA button based on subscription state.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, LayoutGrid, Sparkles, Download } from 'lucide-react';
import { buildCheckoutUrlWithUtms } from '@/hooks/useUtmCapture';
import type { Plan, CurrentSubscription, BillingPeriod } from '@/types';
import { blockInSupportMode } from '@/lib/impersonation';

interface PlanCardProps {
  plan: Plan;
  period: BillingPeriod;
  currentSubscription: CurrentSubscription | null;
  onSubscribe: (plan: Plan) => void;
  onChangePlan: (plan: Plan) => void;
}

function getPriceForPeriod(plan: Plan, period: BillingPeriod): number {
  if (period === 'MONTHLY') return plan.priceMonthly;
  if (period === 'QUARTERLY') return plan.priceQuarterly;
  return plan.priceYearly;
}

const EXCLUSIVE_OFFER_SLUG = 'direct-response';

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  period,
  currentSubscription,
  onSubscribe,
  onChangePlan,
}) => {
  const { t } = useTranslation();

  const isExclusiveOffer = plan.slug === EXCLUSIVE_OFFER_SLUG;

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const totalPrice = getPriceForPeriod(plan, period);
  const divisor = period === 'YEARLY' ? 12 : period === 'QUARTERLY' ? 3 : 1;
  const displayPrice = totalPrice / divisor;
  const priceLabel = t('plans.perMonth');
  const billingNote = period === 'YEARLY'
    ? t('plans.billedAnnually').replace('{price}', formatPrice(totalPrice))
    : period === 'QUARTERLY'
      ? t('plans.billedQuarterly').replace('{price}', formatPrice(totalPrice))
      : null;
  const isCurrentPlan = isExclusiveOffer
    ? currentSubscription?.planId === plan.planId
    : currentSubscription?.planId === plan.planId && currentSubscription?.period === period;
  // Trial (free plan) should not count as a paid subscription —
  // upgrading from trial must go through checkout with payment.
  const isTrialPlan = currentSubscription?.planName?.toLowerCase() === 'trial';
  const hasPaidSubscription = !!currentSubscription && !isTrialPlan;

  const handleClick = () => {
    if (isCurrentPlan) return;
    if (blockInSupportMode()) return;
    if (hasPaidSubscription) {
      onChangePlan(plan);
    } else if (plan.checkoutUrl) {
      window.open(buildCheckoutUrlWithUtms(plan.checkoutUrl), '_blank');
    } else {
      onSubscribe(plan);
    }
  };

  const getButtonLabel = () => {
    if (isCurrentPlan) return t('plans.currentPlan');
    if (hasPaidSubscription) return t('plans.upgrade');
    return t('plans.subscribe');
  };

  // Build structured limit items
  const limitIconClass = cn('h-4 w-4 shrink-0 mt-0.5', isExclusiveOffer ? 'text-amber-500' : 'text-primary');
  const limitItems: { icon: React.ReactNode; label: string }[] = [];

  if (plan.carouselLimit !== undefined && plan.carouselLimit !== 0) {
    limitItems.push({
      icon: <LayoutGrid className={limitIconClass} />,
      label: plan.carouselLimit === -1
        ? t('plans.limits.carouselsUnlimited')
        : t('plans.limits.carousels').replace('{count}', String(plan.carouselLimit)),
    });
  }

  if (plan.generationLimit !== undefined && plan.generationLimit !== 0) {
    limitItems.push({
      icon: <Sparkles className={limitIconClass} />,
      label: plan.generationLimit === -1
        ? t('plans.limits.generationsUnlimited')
        : t('plans.limits.generations').replace('{count}', String(plan.generationLimit)),
    });
  }

  if (plan.exportLimit !== undefined && plan.exportLimit !== 0) {
    limitItems.push({
      icon: <Download className={limitIconClass} />,
      label: plan.exportLimit === -1
        ? t('plans.limits.exportsUnlimited')
        : t('plans.limits.exports').replace('{count}', String(plan.exportLimit)),
    });
  }

  const displayName = isExclusiveOffer ? t('plans.exclusiveOfferDisplayName') : plan.name;

  return (
    <Card className={cn(
      'flex flex-col p-6 h-full transition-shadow relative',
      isExclusiveOffer
        ? 'border-2 border-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.15)]'
        : isCurrentPlan && 'ring-2 ring-primary shadow-lg'
    )}>
      {/* Exclusive offer floating badge */}
      {isExclusiveOffer && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-[11px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap tracking-wide z-10">
          {t('plans.exclusiveOffer')}
        </div>
      )}

      {/* Plan name badge */}
      <div className={cn('mb-4', isExclusiveOffer && 'mt-1')}>
        <span
          className={cn(
            'inline-block px-4 py-1.5 rounded-full text-sm font-semibold border',
            isExclusiveOffer
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black border-transparent'
              : plan.highlightColor
                ? 'text-white'
                : 'text-foreground border-border'
          )}
          style={!isExclusiveOffer && plan.highlightColor ? {
            backgroundColor: plan.highlightColor,
            borderColor: plan.highlightColor,
          } : undefined}
        >
          {displayName}
        </span>
      </div>

      {/* Price */}
      <div className="mb-4">
        <div>
          <span className={cn(
            'text-2xl font-bold',
            isExclusiveOffer ? 'text-amber-500' : 'text-foreground'
          )}>
            {formatPrice(displayPrice)}
          </span>
          <span className="text-sm text-muted-foreground ml-1">
            {priceLabel}
          </span>
        </div>
        {billingNote && (
          <p className="text-xs text-muted-foreground mt-1">{billingNote}</p>
        )}
      </div>

      {/* Structured limits */}
      {limitItems.length > 0 && (
        <ul className="space-y-2 mb-3 pb-3 border-b border-border">
          {limitItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
              {item.icon}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Features (free-text) */}
      <ul className="flex-1 space-y-2.5 my-4">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
            <Check className={cn(
              'h-4 w-4 shrink-0 mt-0.5',
              isExclusiveOffer ? 'text-amber-500' : 'text-primary'
            )} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Exclusive offer warning */}
      {isExclusiveOffer && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 mb-3 text-[11px] text-amber-500/60 leading-relaxed">
          {t('plans.exclusiveOfferWarning')}
        </div>
      )}

      {/* CTA Button */}
      <Button
        onClick={handleClick}
        disabled={isCurrentPlan}
        className={cn(
          'w-full mt-auto',
          isCurrentPlan
            ? 'bg-muted text-muted-foreground cursor-not-allowed'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        )}
      >
        {getButtonLabel()}
      </Button>
    </Card>
  );
};

export default PlanCard;
